import { create } from 'zustand'
import type { Notification } from '../notifications'

interface NotificationsState {
  unreadCount: number
  // createdAt (ISO) de la notificación más reciente que conoce el store —
  // marca de agua genérica a cualquier `kind` futuro (fin de sync, org
  // seguida con contenido nuevo, etc.), no un conteo. Un conteo puede volver
  // a coincidir con un valor anterior por casualidad (leo 2, llegan 2
  // nuevas) y ahí una comparación de conteo no detectaría el cambio real —
  // una fecha de creación nunca retrocede ni se repite entre notificaciones
  // distintas, así que sirve de fuente de verdad confiable para "¿hay algo
  // nuevo desde la última vez que miré?".
  latestAt: string | null
  // Cache de las últimas notificaciones para el dropdown de la campanita —
  // null = todavía no se cargó ni una vez. Se refresca en silencio desde
  // NotificationsPoller cada vez que cambia latestAt, no al abrir el
  // dropdown, para que abrir la campanita nunca muestre un loading.
  items: Notification[] | null
  // Ids marcados como leídos localmente en esta sesión (ej. al entrar al
  // detalle desde el listado completo) — el listado paginado no vuelve a
  // pedir sus datos al volver atrás (cache de navegación de Next.js), así
  // que se corrige acá en vez de depender de un refetch que puede no pasar.
  readOverrides: Set<string>
  // Retorna true si latestAt avanzó respecto al valor previo del store —
  // el store es la única fuente de verdad del "último valor conocido": si
  // en cambio cada consumidor (poller, página de detalle) llevara su propia
  // copia, quedarían desincronizados entre sí (pasó exactamente eso con un
  // useRef local del poller antes de este cambio).
  applyUnreadCount: (data: { count: number; latestAt: string | null }) => boolean
  setItems: (items: Notification[]) => void
  markItemRead: (id: string) => void
  // Tras "marcar todas como leídas": baja el contador a 0 y marca el cache
  // de la campanita sin esperar al próximo tick del poller.
  markAllRead: () => void
}

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  unreadCount: 0,
  latestAt: null,
  items: null,
  readOverrides: new Set(),

  applyUnreadCount: ({ count, latestAt }) => {
    const changed = latestAt !== null && latestAt !== get().latestAt
    set({ unreadCount: count, latestAt })
    return changed
  },

  setItems: (items) => set({ items }),

  markItemRead: (id) =>
    set((state) => ({
      items: state.items?.map((n) => (n._id === id ? { ...n, read: true } : n)) ?? state.items,
      readOverrides: new Set(state.readOverrides).add(id),
    })),

  markAllRead: () =>
    set((state) => ({
      unreadCount: 0,
      items: state.items?.map((n) => ({ ...n, read: true })) ?? state.items,
    })),
}))
